import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import AccountScheduledForDeletion from "@/components/AccountScheduledForDeletion";

/**
 * Drop a `noindex,nofollow` robots meta on every authenticated route so
 * Googlebot doesn't index gated URLs (which would also serve a 401 to the
 * crawler and hurt domain quality). Pages that need their own SEO tags can
 * still override the title/description via their own <Helmet>.
 */
const GatedNoIndex = () => (
  <Helmet>
    <meta name="robots" content="noindex,nofollow" />
  </Helmet>
);

const Gated = () => (
  <>
    <GatedNoIndex />
    <Outlet />
  </>
);

/**
 * Probe the session's AAL and enrolled TOTP factors. Returns a tri-state:
 *   - "loading"  — probe in flight
 *   - "ok"       — no enrolled factor OR already at aal2 OR API unavailable
 *   - "required" — user has a verified TOTP factor but session is aal1.
 *
 * SECURITY: This MUST run on every authenticated route (not just admin
 * surfaces). Otherwise an admin sitting on /login/mfa could open the app
 * via the marketing page / logo and reach /dashboard at AAL1, completely
 * bypassing their second factor. The same risk applies to any learner
 * who has voluntarily enrolled TOTP. Failing open on transient probe
 * errors mirrors AdminRoute — server-side RLS is the ultimate authority.
 */
type MfaGateStatus = "loading" | "ok" | "challenge-required" | "setup-required";

function useMfaSessionGate(userId: string | null | undefined, mustHaveMfa = false) {
  const [state, setState] = useState<{
    userId: string | null;
    mustHaveMfa: boolean;
    status: MfaGateStatus;
  }>(() => ({
    userId: userId ?? null,
    mustHaveMfa,
    status: userId ? "loading" : "ok",
  }));

  const expectedUserId = userId ?? null;
  const currentStatus =
    expectedUserId && (state.userId !== expectedUserId || state.mustHaveMfa !== mustHaveMfa)
      ? "loading"
      : state.status;

  useEffect(() => {
    if (!userId) {
      setState({ userId: null, mustHaveMfa, status: "ok" });
      return;
    }
    setState({ userId, mustHaveMfa, status: "loading" });
    const mfa = supabase.auth?.mfa;
    if (!mfa?.getAuthenticatorAssuranceLevel || !mfa?.listFactors) {
      // MFA API not wired (older clients / unit-test stubs). Allow through.
      setState({ userId, mustHaveMfa, status: "ok" });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [aalRes, factorsRes] = await Promise.allSettled([
          mfa.getAuthenticatorAssuranceLevel(),
          mfa.listFactors(),
        ]);
        if (cancelled) return;
        const aal =
          aalRes.status === "fulfilled"
            ? (aalRes.value as { data?: { currentLevel?: string; nextLevel?: string } })?.data
            : undefined;
        const level = aal?.currentLevel;
        const nextLevel = aal?.nextLevel;
        const factors =
          factorsRes.status === "fulfilled"
            ? (factorsRes.value as { data?: { totp?: Array<{ status?: string }> } })?.data
                ?.totp ?? []
            : [];
        const hasVerified = factors.some((f) => f.status === "verified");
        let status: MfaGateStatus = "ok";
        if (level === "aal2") {
          status = "ok";
        } else if (mustHaveMfa) {
          // Admin/super-admin accounts must not reach learner surfaces at AAL1.
          // If they have a factor (or the AAL endpoint says AAL2 is available),
          // force the code challenge; otherwise only the setup flow is allowed.
          status = hasVerified || nextLevel === "aal2" ? "challenge-required" : "setup-required";
        } else if (hasVerified || nextLevel === "aal2") {
          status = "challenge-required";
        }
        setState({ userId, mustHaveMfa, status });
      } catch {
        if (!cancelled) {
          setState({ userId, mustHaveMfa, status: mustHaveMfa ? "setup-required" : "ok" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, mustHaveMfa]);
  return currentStatus;
}

export const ProtectedRoute = () => {
  const { user, isAdmin, isSuperAdmin, deletionScheduledAt, isLoading } = useAuth();
  const mfaGate = useMfaSessionGate(user?.id ?? null, Boolean(isAdmin || isSuperAdmin));
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Deletion lock takes precedence over all child routes — paywall, admin
  // gate, billing return, everything. The user must restore or sign out.
  if (deletionScheduledAt) {
    return <AccountScheduledForDeletion />;
  }

  // Any user with a verified TOTP factor MUST be at AAL2 before any
  // protected surface renders. Without this, an admin (or learner with
  // MFA enrolled) could hit "Back" on the MFA challenge, return to the
  // public landing, and re-enter the app at AAL1 — fully bypassing the
  // second factor.
  if (mfaGate === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (mfaGate === "challenge-required") {
    const from = location.pathname + location.search;
    return <Navigate to="/login/mfa" replace state={{ from }} />;
  }
  if (mfaGate === "setup-required") {
    if (location.pathname === "/settings") {
      return <Gated />;
    }
    return <MfaSetupRequired />;
  }

  return <Gated />;
};

const MfaSetupRequired = () => (
  <div className="min-h-screen flex items-center justify-center bg-secondary px-6">
    <div className="max-w-md w-full text-center space-y-4 border rounded-lg p-8 bg-card">
      <div className="flex justify-center">
        <div className="h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
          <ShieldCheck className="h-6 w-6" />
        </div>
      </div>
      <h1 className="text-xl font-semibold">Two-factor authentication required</h1>
      <p className="text-sm text-muted-foreground">
        Admin accounts must set up two-factor authentication before using the app.
      </p>
      <Button asChild>
        <Link to="/settings">Set up authenticator</Link>
      </Button>
    </div>
  </div>
);

/**
 * Signed-in app surface.
 *
 * The paywall is no longer a route bounce: any signed-in account reaches the
 * app shell, and certification scoped pages lock themselves in place (see
 * RequireCertification + LockedPageOverlay) when the account holds no active
 * certification. Server-side RLS remains the real access control.
 *
 * This guard still handles the account-deletion state and, through Gated,
 * the noindex meta. Admins and super_admins are unaffected.
 */
export const PaidRoute = () => {
  const { user, deletionScheduledAt, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (deletionScheduledAt) {
    return <AccountScheduledForDeletion />;
  }

  return <Gated />;
};


export const AdminRoute = () => {
  const { user, isAdmin, deletionScheduledAt, isLoading } = useAuth();
  const [aalState, setAalState] = useState<"loading" | "aal1" | "aal2">(
    isAdmin ? "loading" : "aal2",
  );
  // Whether the admin already has a verified TOTP factor — drives whether the
  // gate offers a "Verify now" re-challenge or an "Enroll authenticator" CTA.
  const [hasEnrolledFactor, setHasEnrolledFactor] = useState(false);

  useEffect(() => {
    if (!user || !isAdmin) {
      setAalState("aal2");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [aalRes, factorsRes] = await Promise.allSettled([
          supabase.auth.mfa?.getAuthenticatorAssuranceLevel?.() ?? Promise.resolve(undefined),
          supabase.auth.mfa?.listFactors?.() ?? Promise.resolve(undefined),
        ]);
        if (cancelled) return;
        const level =
          aalRes.status === "fulfilled"
            ? (aalRes.value as { data?: { currentLevel?: string } } | undefined)?.data?.currentLevel
            : undefined;
        const factors =
          factorsRes.status === "fulfilled"
            ? (factorsRes.value as { data?: { totp?: Array<{ status?: string }> } } | undefined)
                ?.data
            : undefined;
        setHasEnrolledFactor((factors?.totp ?? []).some((f) => f.status === "verified"));
        // If the AAL probe failed entirely, fall back to allow — server-side
        // RLS still enforces. Only gate when we actually got back a non-aal2
        // level.
        if (aalRes.status === "rejected") {
          setAalState("aal2");
        } else {
          setAalState(level === "aal2" ? "aal2" : "aal1");
        }
      } catch {
        // Transient probe failure — fall back to allow (server-side RLS still enforces).
        if (!cancelled) setAalState("aal2");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isAdmin]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (deletionScheduledAt) {
    return <AccountScheduledForDeletion />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // MFA enforcement for admins — only gate when the AAL API is available.
  if (supabase.auth.mfa?.getAuthenticatorAssuranceLevel) {
    if (aalState === "loading") {
      return (
        <div className="min-h-screen flex items-center justify-center bg-secondary">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }
    if (aalState === "aal1") {
      return (
        <div className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="max-w-md w-full text-center space-y-4 border rounded-lg p-8 bg-card">
            <div className="flex justify-center">
              <div className="h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
                <ShieldCheck className="h-6 w-6" />
              </div>
            </div>
            <h1 className="text-xl font-semibold">Two-factor authentication required</h1>
            <p className="text-sm text-muted-foreground">
              {hasEnrolledFactor
                ? "Admin actions require a verified second factor on this session. Verify your authenticator code to continue."
                : "Admin actions require a verified second factor. Enroll an authenticator app on your account, then sign in again."}
            </p>
            <div className="flex gap-2 justify-center">
              {hasEnrolledFactor ? (
                <Button asChild>
                  <Link
                    to="/login/mfa"
                    state={{ from: window.location.pathname + window.location.search }}
                  >
                    Verify now
                  </Link>
                </Button>
              ) : (
                <Button asChild>
                  <Link to="/settings">Set up authenticator</Link>
                </Button>
              )}
              <Button asChild variant="outline">
                <Link to="/dashboard">Back to app</Link>
              </Button>
            </div>
          </div>
        </div>
      );
    }
  }

  return <Gated />;
};

export const SuperAdminRoute = () => {
  const { user, isAdmin, isSuperAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to={isAdmin ? "/admin/certifications" : "/dashboard"} replace />;
  }

  return <Gated />;
};
